/**
 * Composes the Courses route guard: prime a token, check the `courses` flag, then
 * check pastoral status.
 *
 * The order is the whole point, and it is load-bearing rather than cosmetic.
 * Route guards run inside router.isReady() — before main.js commits an access
 * token to Vuex — so on a hard load or deep link the store's token is empty for
 * the entire guard phase. The pastoral step calls /v2/profile,
 * /profile/pastoral-mapping and /courses/access; without a primed token all three
 * go out with an empty Bearer, come back 401, and the 401 interceptor ends the
 * session. Clicking into Courses from All Page always worked, because a
 * client-side navigation happens long after the token is committed; refreshing
 * the same page logged the member out.
 *
 * Priming used to happen by accident: requireFeatureFlag fetched a token on its
 * way to evaluating user-targeted flags, but only on the branch it takes while
 * the flag reads false. Turning `courses` on in production removed the accident
 * and the logouts started. Hence an explicit first step that does not depend on
 * which way a flag happens to be set.
 *
 * Dependencies are injected so this ordering can be asserted without importing
 * the router (and with it every page component in the app).
 *
 * @param {Object} deps
 * @param {() => Promise<string|undefined>} deps.ensureToken - primes the store token
 * @param {() => Promise<string|undefined>} deps.requireFlag - resolves to a fallback path when the flag is off
 * @param {(to: Object) => Promise<string|undefined>} deps.requirePastoralStatus
 * @returns {(to: Object) => Promise<string|undefined>} a Vue Router beforeEnter guard
 */
export function createCoursesGuard({ ensureToken, requireFlag, requirePastoralStatus }) {
  return async to => {
    await ensureToken();
    const flagResult = await requireFlag();
    if (flagResult) return flagResult;
    return requirePastoralStatus(to);
  };
}
